/**
 * ip-api.com Geolocation Provider
 * 
 * Direct integration with ip-api.com's free JSON API.
 * No server required - calls the API directly from the client.
 * 
 * @see https://ip-api.com/docs/api:json
 * 
 * LIMITATIONS:
 * - HTTP only (no HTTPS on free tier)
 * - Rate limited: 45 requests/minute
 * - Non-commercial use only
 */

import type { IGeoProvider, GeoLookupResult } from './IGeoProvider';
import { DEFAULT_GEO_RESULT } from './IGeoProvider';
import { createLogger } from '../../utils/logger';
const log = createLogger('IpApiProvider');

const IP_API_BASE_URL = 'http://ip-api.com/json';
const IP_API_FIELDS = 'status,message,query,country,countryCode,city,lat,lon,isp,org,timezone';

export class IpApiProvider implements IGeoProvider {
    readonly name = 'ip-api.com';

    /**
     * Look up the current user's IP and location
     * Calling without a query parameter returns the requester's IP info
     */
    async lookupSelf(): Promise<GeoLookupResult> {
        try {
            log.log(`[${this.name}] Fetching current IP metadata...`);
            const url = `${IP_API_BASE_URL}?fields=${IP_API_FIELDS}`;
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`API request failed: ${response.statusText}`);
            }

            const data = await response.json();
            return this.mapResponse(data);
        } catch (error) {
            log.error(`[${this.name}] Self lookup failed:`, error);
            return DEFAULT_GEO_RESULT;
        }
    }

    /**
     * Look up location for a specific IP address
     * @param ip - IPv4 or IPv6 address to look up
     */
    async lookupIp(ip: string): Promise<GeoLookupResult> {
        try {
            log.log(`[${this.name}] Looking up IP: ${ip}`);
            const url = `${IP_API_BASE_URL}/${ip}?fields=${IP_API_FIELDS}`;
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`API request failed: ${response.statusText}`);
            }

            const data = await response.json();

            // Check for API-level errors (e.g., invalid IP, private range)
            if (data.status === 'fail') {
                log.warn(`[${this.name}] Lookup failed: ${data.message}`);
                return { ...DEFAULT_GEO_RESULT, ip };
            }

            return this.mapResponse(data);
        } catch (error) {
            log.error(`[${this.name}] IP lookup failed for ${ip}:`, error);
            return { ...DEFAULT_GEO_RESULT, ip };
        }
    }

    /**
     * Map ip-api.com response to standardized GeoLookupResult
     * 
     * ip-api.com response format:
     * {
     *   "status": "success",
     *   "country": "Belgium",
     *   "countryCode": "BE",
     *   "city": "Brussels",
     *   "lat": 50.8503,
     *   "lon": 4.3517,
     *   "isp": "Proximus",
     *   "org": "Proximus NV",
     *   "timezone": "Europe/Brussels",
     *   "query": "1.2.3.4"
     * }
     */
    private mapResponse(data: any): GeoLookupResult {
        return {
            ip: data.query || '',
            city: data.city || null,
            country: data.country || null,
            countryCode: data.countryCode || null,
            latitude: data.lat ?? DEFAULT_GEO_RESULT.latitude,
            longitude: data.lon ?? DEFAULT_GEO_RESULT.longitude,
            org: data.org || null,
            isp: data.isp || null,
            timezone: data.timezone || null,
            hostname: null
        };
    }
}
